import { useStore } from '@tanstack/react-form';
import { useMemo, type ReactNode } from 'react';

import { type OptionType } from '@/api/common';
import type { InstanceDetailDataType } from '@/api/organization';
import { useTypedAppFormContext } from '@/components/Form';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxStatus,
} from '@/components/Form/pickers';

type CRMFieldComboboxProps = {
  className?: string;
  'aria-invalid'?: boolean;
  placeholder: string;
  notFoundContent: ReactNode;
  items?: OptionType[];
  disabled?: boolean;
  value?: string;
  onBlur?: () => void;
  onValueChange?: (value: string) => void;
};

const CRMFieldCombobox = ({
  className,
  disabled,
  items,
  value,
  placeholder,
  notFoundContent,
  'aria-invalid': ariaInvalid,
  onBlur,
  onValueChange,
  ...rest
}: CRMFieldComboboxProps) => {
  const form = useTypedAppFormContext({ defaultValues: {} as InstanceDetailDataType });
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  const selectedOption = useMemo(
    () => items?.find((option) => option.id === value) ?? null,
    [items, value],
  );

  return (
    <Combobox
      items={items}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      disabled={disabled || isSubmitting}
      size="medium"
      value={selectedOption}
      onValueChange={(value) => onValueChange?.(value?.id ?? '')}
      {...rest}
    >
      <ComboboxInput
        className={className}
        placeholder={placeholder}
        onBlur={onBlur}
        aria-invalid={ariaInvalid || undefined}
      />
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>{notFoundContent}</ComboboxEmpty>
        <ComboboxList>
          {(item: OptionType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default CRMFieldCombobox;
